import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { faxReceivedApi } from "@/api";
import { format } from "date-fns";
import { FileText, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

const statusLabels: Record<string, string> = {
  completed: "受信完了",
  failed: "失敗",
};

const statusColors: Record<string, string> = {
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const ocrStatusLabels: Record<string, string> = {
  unreviewed: "未レビュー",
  reviewed: "レビュー済",
  approved: "承認済",
};

export default function FaxReceived() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["fax-received", { page }],
    queryFn: () => faxReceivedApi.list({ page: String(page), limit: "50" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText size={20} className="text-orange-500" />
        <h2 className="text-xl font-bold">FAX受信</h2>
        {data && <span className="text-sm text-slate-500">{data.total}件</span>}
      </div>

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">受信日時</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">送信元</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">ステータス</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">ページ数</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">種別</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">OCR</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">要約</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((fax) => (
                  <tr key={fax.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600 text-xs whitespace-nowrap">
                      {format(new Date(fax.receivedAt), "yyyy/MM/dd HH:mm")}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{fax.senderNumber}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[fax.status] || "bg-slate-100 text-slate-600"}`}>
                        {statusLabels[fax.status] || fax.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-center">{fax.pageCount ?? "-"}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{fax.faxType || "-"}</td>
                    <td className="px-4 py-2">
                      {fax.ocrStatus ? (
                        <span className="text-xs text-slate-500">{ocrStatusLabels[fax.ocrStatus] || fax.ocrStatus}</span>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 max-w-[250px] truncate" title={fax.ocrSummary || ""}>
                      {fax.ocrSummary || "-"}
                    </td>
                    <td className="px-4 py-2">
                      {fax.documentId ? (
                        <a
                          href={faxReceivedApi.pdfUrl(fax.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {data?.data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      受信FAXがありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {(page - 1) * data.limit + 1}-{Math.min(page * data.limit, data.total)} / {data.total}件
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page >= data.totalPages}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
